import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from '../../i18n';
import { setLocale } from '../../i18n';
import { SELECTABLE_LOCALES, type SelectableLocale } from '../../i18n/config';
import './LanguageSelector.css';

const LOCALE_LABELS: Record<(typeof SELECTABLE_LOCALES)[number], string> = {
  en: 'EN',
  de: 'DE',
};

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = i18n.language?.split('-')[0] || 'en';
  const activeLocale: SelectableLocale = SELECTABLE_LOCALES.includes(current as SelectableLocale)
    ? (current as SelectableLocale)
    : 'en';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (locale: SelectableLocale) => {
    if (locale !== activeLocale) {
      void setLocale(locale);
    }
    setIsOpen(false);
  };

  return (
    <div ref={ref} className="language-selector">
      <button
        type="button"
        className={`language-selector__toggle${isOpen ? ' language-selector__toggle--open' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={t('common.language.label')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {LOCALE_LABELS[activeLocale]}
      </button>
      {isOpen && (
        <ul className="language-selector__dropdown" role="listbox" aria-label={t('common.language.label')}>
          {SELECTABLE_LOCALES.map((locale) => (
            <li key={locale} role="option" aria-selected={locale === activeLocale}>
              <button
                type="button"
                className={`language-selector__option${locale === activeLocale ? ' language-selector__option--active' : ''}`}
                onClick={() => handleSelect(locale)}
              >
                <span className="language-selector__code">{LOCALE_LABELS[locale]}</span>
                <span className="language-selector__name">{t(`common.language.${locale}`)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
